import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Container,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import {
  IconArrowRight,
  IconBook2,
  IconCalendarEvent,
  IconClock,
  IconFileCheck,
  IconFlask2,
  IconMapPin,
  IconPhone,
  IconShieldCheck,
  IconStethoscope,
} from "@tabler/icons-react";
import {
  BRAND_PALETTES,
  NextGenBrand,
  NextGenBrandIcon,
  type BrandPaletteKey,
} from "@evolys/ui";
import type {
  PublicMarketingLocationDto,
  PublicMarketingSiteDto,
  WeeklyOpeningHoursDto,
} from "@evolys/types";
import { EducationArticle, EducationIndex } from "./EducationPages";
import { BookingPage } from "./BookingPage";
import { CareGateway } from "./CareGateway";
import { MedicalLanding } from "./MedicalLanding";
import { ServiceSwitcher } from "./ServiceSwitcher";
import { isStagingUrl } from "./publicLinks";

const dayLabels: Array<[keyof WeeklyOpeningHoursDto, string]> = [
  ["monday", "Monday"],
  ["tuesday", "Tuesday"],
  ["wednesday", "Wednesday"],
  ["thursday", "Thursday"],
  ["friday", "Friday"],
  ["saturday", "Saturday"],
  ["sunday", "Sunday"],
];

// Returns null when the link would leave the public site for staging, such as
// a portal CTA while the deployment's portal is still a staging host.
function resolveUrl(url: string, portalUrl: string) {
  const resolved =
    url === "/login" || url === "/results"
      ? `${portalUrl.replace(/\/$/, "")}${url}`
      : url;
  return isStagingUrl(resolved) ? null : resolved;
}

function isBookingUrl(url: string) {
  return url === "/book" || url.endsWith("/book") || url.startsWith("/book?");
}

function LocationCard({
  location,
}: {
  location: PublicMarketingLocationDto;
}) {
  const hours = dayLabels.filter(
    ([key]) => Boolean(location.openingHours[key]),
  );

  return (
    <Paper className="location-card" radius="lg">
      <Group justify="space-between" align="flex-start">
        <Box>
          <Badge variant="light" mb="sm">
            {location.isPrimary ? "Main laboratory" : "Collection site"}
          </Badge>
          <Title order={3}>{location.name}</Title>
        </Box>
        <IconMapPin aria-hidden size={26} />
      </Group>
      {location.address ? (
        <Text mt="sm" c="dimmed">
          {location.address}
        </Text>
      ) : null}
      {location.phone ? (
        <Anchor
          className="contact-link"
          href={`tel:${location.phone}`}
          mt="sm"
        >
          <IconPhone size={17} />
          {location.phone}
        </Anchor>
      ) : null}
      {hours.length ? (
        <Box className="hours" mt="lg">
          <Group gap="xs" mb="xs">
            <IconClock size={18} />
            <Text fw={700}>Opening hours</Text>
          </Group>
          {hours.map(([key, label]) => (
            <Group key={key} justify="space-between" gap="lg">
              <Text size="sm">{label}</Text>
              <Text size="sm" fw={600}>
                {location.openingHours[key]}
              </Text>
            </Group>
          ))}
        </Box>
      ) : null}
    </Paper>
  );
}

export function App() {
  const [site, setSite] = useState<PublicMarketingSiteDto | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "missing" | "error">(
    "loading",
  );
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    fetch("/api/public/site-profile", {
      cache: "no-store",
      credentials: "omit",
      headers: { Accept: "application/json" },
      signal: controller.signal,
    })
      .then(async (response) => {
        if (response.status === 404) {
          setStatus("missing");
          return;
        }
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const payload = (await response.json()) as PublicMarketingSiteDto;
        setSite(payload);
        setStatus("ready");
      })
      .catch((error: unknown) => {
        if (
          error instanceof DOMException &&
          error.name === "AbortError"
        ) return;
        setStatus("error");
      });
    return () => controller.abort();
  }, [reloadToken]);

  useEffect(() => {
    if (!site) return;
    const currentPath = window.location.pathname.replace(/\/+$/, "") || "/";
    const isMedical = currentPath === "/medical";
    const isGateway = currentPath === "/";
    document.title = isMedical
      ? "NextGen Medical | Doctor visits in Jamaica"
      : isGateway
        ? "NextGen | Medical care and laboratory services"
        : site.profile.seoTitle;
    const description = document.querySelector<HTMLMetaElement>(
      'meta[name="description"]',
    );
    description?.setAttribute(
      "content",
      isMedical
        ? "NextGen Medical is preparing scheduled doctor visits, equipped clinical rooms, and connected laboratory care in Jamaica."
        : isGateway
          ? "Choose NextGen Medical for scheduled doctor visits or NextGen Diagnostics for laboratory testing and sample collection in Jamaica."
          : site.profile.seoDescription,
    );
  }, [site]);

  const configuredTheme = site?.profile.themeKey;
  const paletteKey = (
    configuredTheme && configuredTheme in BRAND_PALETTES
      ? configuredTheme
      : "teal"
  ) as BrandPaletteKey;
  const palette = BRAND_PALETTES[paletteKey];
  const style = useMemo(
    () =>
      ({
        "--evolys-brand-primary": palette.primary,
        "--evolys-brand-on-primary": palette.onPrimary,
        "--evolys-brand-accent": palette.accent,
        "--evolys-brand-ink": palette.ink,
        "--site-primary": palette.primary,
        "--site-on-primary": palette.onPrimary,
        "--site-accent": palette.accent,
        "--site-ink": palette.ink,
        "--site-surface": palette.surface,
        "--site-muted": palette.muted,
      }) as React.CSSProperties,
    [palette],
  );
  const runtimeConfig = window.__EVOLYS_CONFIG__ as
    | Record<string, string | undefined>
    | undefined;
  const portalUrl = runtimeConfig?.PORTAL_URL ?? "/portal";
  // Fail closed: booking stays hidden unless the deployment explicitly opens it.
  const bookingEnabled = runtimeConfig?.PUBLIC_BOOKING_ENABLED === "true";

  if (status === "loading") {
    return (
      <Box className="status-page" style={style}>
        <Loader color="var(--site-primary)" />
        <Text>Loading laboratory information…</Text>
      </Box>
    );
  }

  if (status !== "ready" || !site) {
    return (
      <Container className="status-page" style={style} size="sm">
        <NextGenBrand subtitle="Laboratory services" />
        <Alert
          className="status-alert"
          color={status === "missing" ? "gray" : "red"}
          title={
            status === "missing"
              ? "Website coming soon"
              : "Website temporarily unavailable"
          }
        >
          {status === "missing"
            ? "This laboratory has not published its public website yet."
            : "Please try again shortly or contact the laboratory directly."}
          <Button
            color={status === "missing" ? "gray" : "red"}
            mt="md"
            size="sm"
            variant="light"
            onClick={() => {
              setStatus("loading");
              setReloadToken((value) => value + 1);
            }}
          >
            Check again
          </Button>
        </Alert>
      </Container>
    );
  }

  const profile = site.profile;
  const primaryUrl = resolveUrl(profile.primaryCtaUrl, portalUrl);
  const secondaryUrl = resolveUrl(profile.secondaryCtaUrl, portalUrl);
  const primaryIsBooking = primaryUrl !== null && isBookingUrl(primaryUrl);
  const showPrimaryCta =
    primaryUrl !== null && (bookingEnabled || !primaryIsBooking);
  const showSecondaryCta =
    secondaryUrl !== null && (bookingEnabled || !isBookingUrl(secondaryUrl));
  const showBookLink = bookingEnabled && !primaryIsBooking;
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  if (path === "/learn") {
    return <EducationIndex site={site} style={style} />;
  }
  if (path.startsWith("/learn/")) {
    return (
      <EducationArticle
        site={site}
        slug={decodeURIComponent(path.slice("/learn/".length))}
        style={style}
      />
    );
  }
  if (path === "/book") {
    if (!bookingEnabled) {
      return (
        <Container className="status-page" style={style} size="sm">
          <NextGenBrand name={site.laboratoryName} subtitle={site.tagline} />
          <Alert
            className="status-alert"
            color="gray"
            title="Online booking is coming soon"
          >
            {profile.contactPhone
              ? `Please call us on ${profile.contactPhone} to arrange a visit or sample collection.`
              : "Please contact us directly to arrange a visit or sample collection."}
            <Group mt="md" gap="sm">
              {profile.contactPhone ? (
                <Button
                  component="a"
                  href={`tel:${profile.contactPhone}`}
                  size="sm"
                  leftSection={<IconPhone size={16} />}
                >
                  Call us
                </Button>
              ) : null}
              <Button component="a" href="/" size="sm" variant="light">
                Back to home
              </Button>
            </Group>
          </Alert>
        </Container>
      );
    }
    return <BookingPage site={site} style={style} />;
  }
  if (path === "/") {
    return <CareGateway site={site} style={style} />;
  }
  if (path === "/medical") {
    return <MedicalLanding site={site} style={style} />;
  }

  return (
    <Box className="site" style={style}>
      <header className="site-header">
        <Container size="xl">
          <Group
            className="site-header-row"
            justify="space-between"
            py="md"
            wrap="nowrap"
          >
            <Box className="site-header-brand">
              {profile.logoUrl ? (
                <Group gap="sm" wrap="nowrap">
                  <img
                    className="custom-logo"
                    src={profile.logoUrl}
                    alt=""
                  />
                  <Text fw={800}>{site.laboratoryName}</Text>
                </Group>
              ) : (
                <NextGenBrand
                  name={site.laboratoryName}
                  subtitle={site.tagline}
                  imageSize={46}
                />
              )}
            </Box>
            <Group gap="xl" visibleFrom="sm">
              <Anchor href="#services">Services</Anchor>
              <Anchor href="#locations">Locations</Anchor>
              <Button
                component="a"
                href="/learn"
                variant="subtle"
                leftSection={<IconBook2 size={17} />}
                className="learn-nav-link"
              >
                Learn
              </Button>
              {showBookLink ? (
                <Button
                  component="a"
                  href="/book"
                  variant="subtle"
                  leftSection={<IconCalendarEvent size={17} />}
                >
                  Book a visit
                </Button>
              ) : null}
              <Anchor href="#contact">Contact</Anchor>
              {showPrimaryCta ? (
                <Button
                  component="a"
                  href={primaryUrl}
                  variant="filled"
                >
                  {profile.primaryCtaLabel}
                </Button>
              ) : null}
            </Group>
            <Group
              className="mobile-site-actions"
              gap={6}
              hiddenFrom="sm"
              wrap="nowrap"
            >
              <Button
                component="a"
                href="/learn"
                variant="subtle"
                aria-label="Browse health education"
              >
                Learn
              </Button>
              {bookingEnabled ? (
                <Button
                  component="a"
                  href="/book"
                  variant="light"
                  aria-label="Book a sample collection"
                >
                  Book
                </Button>
              ) : null}
            </Group>
          </Group>
        </Container>
        <ServiceSwitcher active="diagnostics" />
      </header>

      <main>
        <section className="hero">
          <div className="molecular-grid" aria-hidden="true">
            {Array.from({ length: 8 }).map((_, index) => (
              <span key={index} />
            ))}
          </div>
          <Container size="xl" className="hero-container">
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing={80}>
              <Stack justify="center" gap="xl" className="hero-copy">
                <Text className="eyebrow">{profile.heroEyebrow}</Text>
                <Title order={1}>{profile.heroTitle}</Title>
                <Text className="hero-body">{profile.heroBody}</Text>
                <Group className="hero-actions">
                  {showBookLink ? (
                    <Button
                      component="a"
                      href="/book"
                      size="lg"
                      leftSection={<IconCalendarEvent size={18} />}
                    >
                      Book a visit
                    </Button>
                  ) : null}
                  {showPrimaryCta ? (
                    <Button
                      component="a"
                      href={primaryUrl}
                      size="lg"
                      variant="light"
                      rightSection={<IconArrowRight size={18} />}
                    >
                      {profile.primaryCtaLabel}
                    </Button>
                  ) : null}
                  {showSecondaryCta ? (
                    <Button
                      component="a"
                      href={secondaryUrl}
                      size="lg"
                      variant="subtle"
                    >
                      {profile.secondaryCtaLabel}
                    </Button>
                  ) : null}
                </Group>
              </Stack>
              <Box className="diagnostic-pathway">
                <Box className="pathway-mark">
                  <NextGenBrandIcon size={160} />
                </Box>
                <div className="pathway-line" />
                <Paper className="pathway-card pathway-card-one">
                  <IconFlask2 size={24} />
                  <div>
                    <Text fw={800}>Collect</Text>
                    <Text size="sm" c="dimmed">
                      Convenient specimen collection
                    </Text>
                  </div>
                </Paper>
                <Paper className="pathway-card pathway-card-two">
                  <IconFileCheck size={24} />
                  <div>
                    <Text fw={800}>Verify</Text>
                    <Text size="sm" c="dimmed">
                      Careful result review
                    </Text>
                  </div>
                </Paper>
                <Paper className="pathway-card pathway-card-three">
                  <IconShieldCheck size={24} />
                  <div>
                    <Text fw={800}>Deliver</Text>
                    <Text size="sm" c="dimmed">
                      Secure digital access
                    </Text>
                  </div>
                </Paper>
              </Box>
            </SimpleGrid>
          </Container>
        </section>

        <section className="about">
          <Container size="xl">
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing={70}>
              <Box>
                <Text className="section-label">Why choose us</Text>
                <Title order={2}>{profile.aboutTitle}</Title>
              </Box>
              <Text className="section-body">{profile.aboutBody}</Text>
            </SimpleGrid>
          </Container>
        </section>

        <section className="services" id="services">
          <Container size="xl">
            <Text className="section-label">What we do</Text>
            <Title order={2}>{profile.servicesTitle}</Title>
            <Text className="section-intro">{profile.servicesBody}</Text>
            <SimpleGrid cols={{ base: 1, md: 3 }} mt={50}>
              {[
                {
                  icon: IconFlask2,
                  title: "Laboratory testing",
                  text: "Routine and specialist testing coordinated through one trusted laboratory.",
                },
                {
                  icon: IconStethoscope,
                  title: "Clinician support",
                  text: "Clear reports and dependable result access for the professionals guiding your care.",
                },
                {
                  icon: IconShieldCheck,
                  title: "Secure results",
                  text: "Privacy-conscious digital delivery designed to keep sensitive health information protected.",
                },
              ].map(({ icon: Icon, title, text }) => (
                <Paper key={title} className="service-card">
                  <Box className="service-icon">
                    <Icon size={25} />
                  </Box>
                  <Title order={3}>{title}</Title>
                  <Text c="dimmed">{text}</Text>
                </Paper>
              ))}
            </SimpleGrid>
          </Container>
        </section>

        {profile.showLocations && site.locations.length ? (
          <section className="locations" id="locations">
            <Container size="xl">
              <Text className="section-label">Find us</Text>
              <Group
                className="locations-heading"
                justify="space-between"
                align="flex-end"
                mb={40}
              >
                <Title order={2}>Locations and opening hours</Title>
                <Text c="dimmed">
                  Choose the site that works best for you.
                </Text>
              </Group>
              <SimpleGrid cols={{ base: 1, md: 2, lg: 3 }}>
                {site.locations.map((location) => (
                  <LocationCard key={location.name} location={location} />
                ))}
              </SimpleGrid>
            </Container>
          </section>
        ) : null}

        <section className="contact" id="contact">
          <Container size="xl">
            <Paper className="contact-panel">
              <Stack gap="lg">
                <Text className="section-label">Talk with our team</Text>
                <Title order={2}>
                  Need help choosing a test or collection site?
                </Title>
                <Text>
                  Contact the laboratory for specimen requirements, opening
                  hours, and result-access support.
                </Text>
                <Group className="contact-actions">
                  {profile.contactPhone ? (
                    <Button
                      component="a"
                      href={`tel:${profile.contactPhone}`}
                      leftSection={<IconPhone size={18} />}
                    >
                      {profile.contactPhone}
                    </Button>
                  ) : null}
                  {profile.contactEmail ? (
                    <Button
                      component="a"
                      href={`mailto:${profile.contactEmail}`}
                      variant="white"
                    >
                      {profile.contactEmail}
                    </Button>
                  ) : null}
                  {profile.whatsAppNumber ? (
                    <Button
                      component="a"
                      href={`https://wa.me/${profile.whatsAppNumber.replace(/\D/g, "")}`}
                      target="_blank"
                      rel="noreferrer"
                      variant="white"
                    >
                      WhatsApp
                    </Button>
                  ) : null}
                </Group>
              </Stack>
            </Paper>
          </Container>
        </section>
      </main>

      <footer>
        <Container size="xl">
          <Group className="site-footer-row" justify="space-between" py="xl">
            <NextGenBrand
              name={site.laboratoryName}
              compact
              subtitle={site.tagline}
            />
            <Text size="sm" c="dimmed">
              © {new Date().getFullYear()} {site.laboratoryName}
            </Text>
          </Group>
        </Container>
      </footer>
    </Box>
  );
}
